import React, { useState, useMemo } from 'react';
import { QueueStore } from '../../queues/store/queue-store';
import {
    CadNextSocketListener,
    createStreamListener,
    useSocketStatus,
    useStream,
} from '../../socket/use-stream';
import { useEventNotificationsStore } from '../../event-notifications/use-event-notifications';
import { useApiClient } from '@m43/core';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { EventQueueRowData } from '../../socket/stream-types';
import {
    EventQueueEnum,
    NudgeObject,
    QueueColumnFilter,
    QueueViewsResource,
} from '@mark43/cad-api';
import { mapQueueClassesToAgencyTypeGlobalAttributes } from '../../queues/utils/map-queue-classes-to-agency-type-global-attributes';
import { toBooleanMap } from '../../common/helpers/utils';
import { WorkspacePanel } from '../../panels/workspace-panel';
import {
    WorkspacePanelWithQueue,
    workspacePanelIsQueue,
} from '../../panels/workspace-queue-exists';
import { some } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@cad-arc';
import { useAedStore } from '../../automatic-event-dispatch/use-aed-store';
import { handleStoreUpdates } from '../../queues/utils/handle-store-updates';
import { PromiseBuffer } from '../../common/promises/promise-buffer';
import { useDispatchAreaStore } from '../../dispatch-areas/use-dispatch-areas';

function getDoesEventNudgePassDispatchAreaFilter(
    eventNudge: NudgeObject,
    dispatchAreasMap: Record<number, boolean>
): boolean {
    return (
        (!!eventNudge.d && dispatchAreasMap[eventNudge.d]) ||
        (!!eventNudge.unitds &&
            some(eventNudge.unitds, (unitDispatchAreaId) => dispatchAreasMap[unitDispatchAreaId]))
    );
}

export const filterEventIdsToUpdateFromEventNudges = (
    eventNudge: NudgeObject,
    workspacePanel: WorkspacePanelWithQueue,
    dispatchAreasMap: Record<number, boolean>,
    queueAgencyTypes: number[]
): boolean => {
    const passesAssignmentStatusFilter =
        workspacePanel.queue.assignmentStatus === undefined ||
        (workspacePanel.queue.assignmentStatus === true && eventNudge.u) ||
        (workspacePanel.queue.assignmentStatus === false && !eventNudge.u);
    const passesAgencyTypeFilter = eventNudge.g && queueAgencyTypes.includes(eventNudge.g);
    const passesDispatchAreaFilter = getDoesEventNudgePassDispatchAreaFilter(
        eventNudge,
        dispatchAreasMap
    );
    const passesEventQueueFilter =
        (workspacePanel.queue.eventQueue === 'DISPATCH' && typeof eventNudge.q === 'undefined') ||
        EventQueueEnum[workspacePanel.queue.eventQueue!].value === eventNudge.q;
    return !!(
        passesAssignmentStatusFilter &&
        passesAgencyTypeFilter &&
        passesDispatchAreaFilter &&
        passesEventQueueFilter
    );
};

export const filterEventIdsToEvictFromEventNudges = (
    eventNudge: NudgeObject,
    workspacePanel: WorkspacePanelWithQueue,
    dispatchAreasMap: Record<number, boolean>
): boolean => {
    // event no longer matches dispatch area filter, evict it
    if (!getDoesEventNudgePassDispatchAreaFilter(eventNudge, dispatchAreasMap)) {
        return true;
    }
    // event is unassigned, but this queue is for assigned events
    if (workspacePanel.queue.assignmentStatus === true && typeof eventNudge.u === 'undefined') {
        return true;
    }
    // event is assigned, but this queue is for unassigned events
    if (workspacePanel.queue.assignmentStatus === false && typeof eventNudge.u !== 'undefined') {
        return true;
    }
    // event's eventQueue is undefined/missing and the queue isn't dispatch OR
    // event's eventQueue (TRU vs Dispatch) does not match the queue
    if (
        (workspacePanel.queue.eventQueue !== 'DISPATCH' && typeof eventNudge.q === 'undefined') ||
        EventQueueEnum[workspacePanel.queue.eventQueue!].value !== eventNudge.q
    ) {
        return true;
    }
    return false;
};

/**
 * This should never be imported directly outside of event-queue-state-provider
 */
export function useDeprecatedIntegratedEventQueueStore({
    workspacePanel,
    dispatchAreaIds,
    eventQueueFilters,
}: {
    workspacePanel: WorkspacePanel;
    dispatchAreaIds: number[];
    eventQueueFilters: QueueColumnFilter[];
}): QueueStore<EventQueueRowData> {
    workspacePanelIsQueue(workspacePanel);
    const apiClient = useApiClient();
    const dispatchAreaStore = useDispatchAreaStore();
    const enabledFeatures = useEnabledFeatures();
    const eventNotificationsStore = useEventNotificationsStore();
    const aedStore = useAedStore();
    const [promiseBuffer] = React.useState(
        () =>
            new PromiseBuffer<number>({
                delay: 100,
                callback: (inputs) => {
                    return fetchEventsUpdatesByIds(
                        inputs,
                        dispatchAreaStore.getMonitoredAndOwnedDispatchAreaIds(),
                        eventQueueFilters
                    );
                },
            })
    );

    const store = useState(
        () =>
            new QueueStore<EventQueueRowData>({
                workspacePanel,
                eventNotificationsStore,
                playAedSoundForEvent: (eventId) => {
                    if (enabledFeatures.dispatchPlayAlertOnAedEvent) {
                        aedStore.playSoundForEvent(eventId);
                    }
                },
            })
    )[0];

    const queueAgencyTypes: number[] = React.useMemo(() => {
        return mapQueueClassesToAgencyTypeGlobalAttributes(workspacePanel.queue.queueClasses);
    }, [workspacePanel.queue.queueClasses]);

    const dispatchAreasMap = React.useMemo(() => {
        return toBooleanMap(dispatchAreaIds);
    }, [dispatchAreaIds]);

    const [debouncedEventQueueFilters] = useDebounce(eventQueueFilters, 300);

    const getInitialQueueStateQuery = useQuery({
        queryKey: [
            'get-initial-event-queue-views',
            store.workspacePanel.queue.id,
            dispatchAreaIds,
            debouncedEventQueueFilters,
        ],
        queryFn() {
            promiseBuffer.stop();
            promiseBuffer.reset();
            return apiClient.request<QueueViewsResource.GetActiveEventQueueViews>({
                method: 'POST',
                url: 'queue_views/event_queue_views/',
                data: {
                    dispatchAreaIds,
                    agencyTypeGlobalAttrIds: queueAgencyTypes,
                    eventQueue: store.workspacePanel.queue.eventQueue,
                    assignmentStatus: store.workspacePanel.queue.assignmentStatus,
                    queueColumnFilters: eventQueueFilters ?? [],
                },
            });
        },
        onSuccess(response) {
            if (response.data.data) {
                store.setRows(response.data.data);
            }
            promiseBuffer.start();
        },
        onError() {
            store.setIsError(true);
        },
        notifyOnChangeProps: [],
        enabled: false,
    });

    const refetch = getInitialQueueStateQuery.refetch;

    const socketStatusListener: CadNextSocketListener = React.useMemo(
        () => ({
            onSocketStatusChange: (socketStatusChangeEvent) => {
                if (socketStatusChangeEvent.status === 'CONNECTED') {
                    refetch();
                }
            },
        }),
        [refetch]
    );

    const { getSocketStatus } = useSocketStatus(socketStatusListener);

    React.useEffect(() => {
        if (getSocketStatus() === 'CONNECTED') {
            refetch();
        }
    }, [dispatchAreaIds, refetch, getSocketStatus]);

    const fetchEventsUpdatesByIds = React.useMemo(() => {
        return (
            eventIds: number[],
            dispatchAreaIds: number[],
            eventQueueFilters: QueueColumnFilter[]
        ) => {
            return apiClient
                .request<QueueViewsResource.GetActiveEventQueueViewsByIdsAndOptions>({
                    method: 'POST',
                    url: 'queue_views/event_queue_views/ids/options',
                    data: {
                        dispatchAreaIds,
                        agencyTypeGlobalAttrIds: queueAgencyTypes,
                        eventQueue: store.workspacePanel.queue.eventQueue,
                        assignmentStatus: store.workspacePanel.queue.assignmentStatus,
                        queueColumnFilters: eventQueueFilters ?? [],
                        eventIds,
                    },
                })
                .then((response) => {
                    const eventQueueViews = response.data.data;
                    handleStoreUpdates(store, eventIds, eventQueueViews);
                });
        };
    }, [apiClient, store, queueAgencyTypes]);

    const getEventIdsToUpdateFromEventNudges = React.useMemo(() => {
        return (eventNudges: NudgeObject[] = []): number[] => {
            return eventNudges
                .filter((eventNudge) => {
                    return filterEventIdsToUpdateFromEventNudges(
                        eventNudge,
                        workspacePanel,
                        dispatchAreasMap,
                        queueAgencyTypes
                    );
                })
                .map((eventNudge) => eventNudge.id);
        };
    }, [dispatchAreasMap, queueAgencyTypes, workspacePanel]);

    const getEventIdsToEvictFromEventNudges = React.useMemo(() => {
        return (eventNudges: NudgeObject[] = []): number[] => {
            return eventNudges
                .filter((eventNudge) => {
                    if (!store.hasRowById(eventNudge.id)) {
                        return false;
                    }
                    return filterEventIdsToEvictFromEventNudges(
                        eventNudge,
                        workspacePanel,
                        dispatchAreasMap
                    );
                })
                .map((eventNudge) => eventNudge.id);
        };
    }, [dispatchAreasMap, store, workspacePanel]);

    const getEventIdsInStoreFromNonEventNudges = React.useMemo(() => {
        return (nonEventNudges: NudgeObject[] = []): number[] => {
            return nonEventNudges
                .map((nonEventNudge) => {
                    return nonEventNudge.e;
                })
                .filter((eventId) => !!eventId && store.hasRowById(eventId)) as number[];
        };
    }, [store]);

    const getPreviousEventIdsInStoreFromUnitStatusNudges = React.useMemo(() => {
        return (unitStatusNudges: NudgeObject[] = []): number[] => {
            return unitStatusNudges
                .map((unitStatusNudge) => {
                    return unitStatusNudge.pe;
                })
                .filter((eventId) => !!eventId && store.hasRowById(eventId)) as number[];
        };
    }, [store]);

    const listener = useMemo(() => {
        if (enabledFeatures.updateEventQueueStreamEnabled) {
            return createStreamListener({
                streamNames: ['updateEventQueue'],
                onMessage: (message) => {
                    const eventIdsToUpdate = message.payload.updateEventQueueDtos.map(
                        (dto) => dto.eventId
                    );
                    if (eventIdsToUpdate.length > 0) {
                        for (const eventIdToUpdate of eventIdsToUpdate) {
                            promiseBuffer.enqueue(eventIdToUpdate);
                        }
                    }
                },
            });
        }
        return createStreamListener({
            streamNames: ['nudge'],
            onMessage: (message) => {
                const eventIdsToUpdate = [
                    ...getEventIdsToUpdateFromEventNudges(message.payload.map['E']),
                    ...getEventIdsInStoreFromNonEventNudges(message.payload.map['HU']),
                    ...getEventIdsInStoreFromNonEventNudges(message.payload.map['USS']),
                    ...getEventIdsInStoreFromNonEventNudges(message.payload.map['UUL']),
                    ...getEventIdsInStoreFromNonEventNudges(message.payload.map['AERC']),
                    ...getPreviousEventIdsInStoreFromUnitStatusNudges(message.payload.map['USS']),
                    ...getEventIdsToEvictFromEventNudges(message.payload.map['E'] ?? []),
                    ...getEventIdsInStoreFromNonEventNudges(message.payload.map['EC'] ?? []),
                ];
                if (eventIdsToUpdate.length > 0) {
                    for (const eventIdToUpdate of eventIdsToUpdate) {
                        promiseBuffer.enqueue(eventIdToUpdate);
                    }
                }
            },
        });
    }, [
        enabledFeatures.updateEventQueueStreamEnabled,
        promiseBuffer,
        getEventIdsToUpdateFromEventNudges,
        getEventIdsInStoreFromNonEventNudges,
        getPreviousEventIdsInStoreFromUnitStatusNudges,
        getEventIdsToEvictFromEventNudges,
    ]);

    // @ts-expect-error sockets-mark43 type issues dont work with dynamic streamNames in socketlistener
    useStream(listener);

    return store;
}
